import React, { useEffect, useMemo, useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import withStyles from 'react-jss';
import { useDispatch, useSelector } from 'react-redux';
import { Button, Input, Popconfirm, Table, Tooltip } from 'antd';
import { DeleteOutlined, EditOutlined, FormOutlined } from '@ant-design/icons';
import Modal from 'antd/lib/modal/Modal';
import { Controller, useForm } from 'react-hook-form';
import moment from 'moment';

import styles from '../styles';
import { errorNotification, successNotification } from '../Snackbar';
import GetField from '../Components/GetField';
import {
  getFactories,
  clearFactories,
  getFactory,
  clearFactory,
} from '../Actions';
import Api from '../Api';
import {
  sorter,
  onFilter,
  filterDropdown,
  filterIcon,
  get,
  dateSorter,
  onDateFilter,
  dateFilterDropdown,
} from '../utils';
import Breadcrumbs from '../Components/Breadcrumbs';
import constants from '../constants';

const AntInput = GetField(Input);

const Factory = props => {
  const { classes } = props;
  const dispatch = useDispatch();

  const { control, getValues, reset } = useForm();

  const auth = useSelector(state => state.auth);
  const permissions = useMemo(() => get(auth, ['permissions'], []), [auth]);
  const factories = useSelector(state => state.factories);
  const factory = useSelector(state => state.factory);

  const [viewModal, setViewModal] = useState(false);
  const [recordId, setRecordId] = useState(null);
  const [confirmLoading, setConfirmLoading] = useState(false);

  useEffect(() => {
    dispatch(getFactories());
    return () => {
      dispatch(clearFactories());
    };
  }, []);

  useEffect(() => {
    reset(factory);
  }, [factory]);

  const onDelete = useCallback(async id => {
    try {
      await Api.delete(`/dropdowns/factories/${id}`);
      successNotification('Factory deleted');
      dispatch(getFactories());
    } catch (err) {
      errorNotification(err);
    }
  }, []);

  const onSubmit = useCallback(async () => {
    try {
      const formValues = getValues();
      setConfirmLoading(true);
      await Api.post('/dropdowns/factories', formValues);
      setConfirmLoading(false);
      reset();
      setViewModal(false);
      successNotification('Factory created');
      dispatch(clearFactory());
      dispatch(getFactories());
    } catch (err) {
      setConfirmLoading(false);
      errorNotification(err);
    }
  }, []);

  const onEditSubmit = useCallback(async () => {
    try {
      const formValues = getValues();
      setConfirmLoading(true);
      await Api.put(`/dropdowns/factories/${recordId}`, formValues);
      setConfirmLoading(false);
      reset();
      setViewModal(false);
      successNotification('Factory updated');
      dispatch(clearFactory());
      dispatch(getFactories());
    } catch (err) {
      setConfirmLoading(false);
      errorNotification(err);
    }
  }, [recordId]);

  const showModal = useCallback(() => {
    setRecordId(null);
    setViewModal(true);
  }, []);

  const showEditModal = useCallback(id => {
    setRecordId(id);
    dispatch(getFactory(id));
    setViewModal(true);
  }, []);

  const hideModal = useCallback(() => {
    setRecordId(null);
    dispatch(clearFactory());
    setViewModal(false);
  }, []);

  const updateColumn = useMemo(
    () => record =>
      (
        <>
          {permissions.includes(constants.permissions.updateFactory) ? (
            <Tooltip placement="bottom" title="Update">
              <EditOutlined
                className={classes.tableIcon}
                onClick={() => showEditModal(record.id)}
              />
            </Tooltip>
          ) : null}
        </>
      ),
    []
  );

  const deleteColumn = useMemo(
    () => record =>
      (
        <>
          {permissions.includes(constants.permissions.deleteFactory) ? (
            <Tooltip placement="bottom" title="Delete">
              <Popconfirm
                title="Are you sure you want to delete?"
                onConfirm={() => onDelete(record.id)}
                okText="Yes"
                cancelText="No"
              >
                <DeleteOutlined className={classes.tableIcon} />
              </Popconfirm>
            </Tooltip>
          ) : null}
        </>
      ),
    []
  );

  const columns = useMemo(() => {
    const column = [
      {
        title: 'Factory',
        dataIndex: 'name',
        filterDropdown,
        filterIcon,
        sorter: sorter('name'),
        onFilter: onFilter('name'),
      },
      {
        title: 'Description',
        dataIndex: 'description',
        filterDropdown,
        filterIcon,
        sorter: sorter('description'),
        onFilter: onFilter('description'),
      },
    ];

    if (permissions.includes(constants.permissions.updateFactory)) {
      column.push({
        title: 'Update',
        width: '7%',
        render: updateColumn,
      });
    }

    if (permissions.includes(constants.permissions.deleteFactory)) {
      column.push({
        title: 'Delete',
        width: '6%',
        render: deleteColumn,
      });
    }

    column.push(
      {
        title: 'Created',
        children: [
          {
            title: 'By',
            dataIndex: 'createdBy',
            sorter: sorter('createdBy'),
            onFilter: onFilter('createdBy'),
            filterDropdown,
            filterIcon,
          },
          {
            title: 'Date',
            dataIndex: 'createdAt',
            sorter: dateSorter('createdAt'),
            onFilter: onDateFilter('createdAt'),
            filterDropdown: dateFilterDropdown,
            filterIcon,
            render: createdAt =>
              createdAt ? moment(createdAt).format('DD MMM YYYY') : null,
          },
        ],
      },
      {
        title: 'Last Updated',
        children: [
          {
            title: 'By',
            dataIndex: 'updatedBy',
            sorter: sorter('updatedBy'),
            onFilter: onFilter('updatedBy'),
            filterDropdown,
            filterIcon,
          },
          {
            title: 'Date',
            dataIndex: 'updatedAt',
            sorter: dateSorter('updatedAt'),
            onFilter: onDateFilter('updatedAt'),
            filterDropdown: dateFilterDropdown,
            filterIcon,
            render: updatedAt =>
              updatedAt ? moment(updatedAt).format('DD MMM YYYY') : null,
          },
        ],
      }
    );

    return column;
  }, []);

  const rowKey = useMemo(() => record => record.id, []);

  const pagination = useMemo(
    () => ({
      defaultPageSize: 20,
      position: ['bottomLeft'],
      showTotal: total => `Total ${total} Records`,
    }),
    []
  );

  const scroll = useMemo(() => ({ x: 1024, y: window.innerHeight - 300 }), []);

  return (
    <>
      <Breadcrumbs
        moduleName="Dropdowns"
        moduleLink="/dropdowns/factories"
        subModuleName="Factory"
      />
      <div className={classes.tableStyle}>
        {permissions.includes(constants.permissions.createFactory) ? (
          <Button className={classes.createButton} onClick={showModal}>
            <FormOutlined /> Create
          </Button>
        ) : null}
        <Table
          bordered
          size="small"
          columns={columns}
          dataSource={factories}
          rowKey={rowKey}
          pagination={pagination}
          scroll={scroll}
        />
      </div>
      <Modal
        title="Factory form"
        visible={viewModal}
        onCancel={hideModal}
        confirmLoading={confirmLoading}
        centered
        maskClosable={false}
        width="30%"
        onOk={recordId ? onEditSubmit : onSubmit}
        okText="Submit"
      >
        <Controller
          name="name"
          control={control}
          render={({ field }) => (
            <AntInput
              allowClear
              label="Factory"
              placeholder="Factory"
              {...field}
            />
          )}
        />
        <Controller
          name="description"
          control={control}
          render={({ field }) => (
            <AntInput
              allowClear
              label="Description"
              placeholder="Description"
              {...field}
            />
          )}
        />
      </Modal>
    </>
  );
};

Factory.propTypes = {
  classes: PropTypes.object.isRequired,
};

export default withStyles(styles)(Factory);
